import { Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import type { Control } from 'react-hook-form'
import { TextField } from '@chillout/ui'
import { AdminPill } from '../../../shared/AdminPill'
import { RoleToggle } from '../RoleToggle'

interface EditCashierFormProps {
  control: Control<{ name: string; email: string; role: 'staff' | 'manager' }>
  effectiveStatus: 'active' | 'inactive' | undefined
  isInactive: boolean
  onResetPin: () => void
}

export function EditCashierForm({ control, effectiveStatus, isInactive, onResetPin }: EditCashierFormProps) {
  const { t } = useTranslation()
  return (
    <>
      <Controller
        control={control}
        name="name"
        render={({ field, fieldState }) => (
          <TextField
            label={t('operations.editCashier.nameLabel')}
            placeholder={t('operations.editCashier.namePlaceholder')}
            value={field.value}
            onChangeText={field.onChange}
            onBlur={field.onBlur}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState }) => (
          <TextField
            label={t('operations.editCashier.emailLabel')}
            placeholder={t('operations.editCashier.emailPlaceholder')}
            value={field.value}
            onChangeText={field.onChange}
            onBlur={field.onBlur}
            error={fieldState.error?.message}
            keyboardType="email-address"
            autoCapitalize="none"
          />
        )}
      />
      <Controller
        control={control}
        name="role"
        render={({ field }) => (
          <RoleToggle
            label={t('operations.editCashier.roleLabel')}
            value={field.value}
            staffLabel={t('operations.roles.staff')}
            managerLabel={t('operations.roles.manager')}
            onChange={field.onChange}
          />
        )}
      />
      <View className="flex-row items-center justify-between">
        <Text className="text-sm font-medium text-primary">
          {t('operations.editCashier.statusLabel')}
        </Text>
        {effectiveStatus && (
          <AdminPill
            label={t(`operations.statuses.${effectiveStatus}`)}
            tone={effectiveStatus}
          />
        )}
      </View>
      {isInactive && (
        <Text className="text-sm font-normal text-secondary">
          {t('operations.editCashier.inactiveNote')}
        </Text>
      )}
      {!isInactive && (
        <>
          <View className="h-px bg-border" />
          <View className="gap-2 pb-2">
            <Text className="text-sm font-semibold text-primary">
              {t('operations.editCashier.pinAccessLabel')}
            </Text>
            <Text className="text-sm font-normal text-secondary">
              {t('operations.editCashier.pinAccessDescription')}
            </Text>
            <Pressable
              onPress={onResetPin}
              accessibilityRole="button"
              accessibilityLabel={t('operations.editCashier.resetPinButton')}
              className="self-start"
            >
              <Text className="text-sm font-semibold text-accent underline">
                {t('operations.editCashier.resetPinButton')}
              </Text>
            </Pressable>
          </View>
        </>
      )}
    </>
  )
}
